import { Alert, Box, Button, MenuItem, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography } from "@mui/material";
import { FC, useCallback, useEffect, useMemo, useState } from "react";
import { getTrainingChecklist, TrainingChecklistItem, updateTrainingChecklist } from "src/apis/upsell";
import SkeletonLoad from "src/pages/components/SkeletonLoad";
import { Id } from "src/utils/types";

const statusOptions: TrainingChecklistItem["status"][] = ["Realizado", "Pendente"];

type TrainingChecklistTabProps = {
  companyId: Id;
};

const TrainingChecklistTab: FC<TrainingChecklistTabProps> = ({ companyId }) => {
  const [trainings, setTrainings] = useState<TrainingChecklistItem[]>([]);
  const [initialValues, setInitialValues] = useState<TrainingChecklistItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string>("");
  const [success, setSuccess] = useState<string>("");

  const hasChanges = useMemo(() => JSON.stringify(trainings) !== JSON.stringify(initialValues), [trainings, initialValues]);

  const fetchChecklist = useCallback(async () => {
    if (!companyId) return;

    setIsLoading(true);
    setError("");
    try {
      const response = await getTrainingChecklist(companyId);
      setTrainings(response.trainings);
      setInitialValues(response.trainings);
    } catch (err: any) {
      setError(err?.message || "Erro ao carregar os treinamentos sugeridos");
    } finally {
      setIsLoading(false);
    }
  }, [companyId]);

  useEffect(() => {
    fetchChecklist();
  }, [fetchChecklist]);

  const handleChange = (index: number, field: keyof TrainingChecklistItem, value: string) => {
    setTrainings((prev) =>
      prev.map((training, idx) =>
        idx === index
          ? {
              ...training,
              [field]: value,
            }
          : training
      )
    );
  };

  const handleSave = async () => {
    if (!companyId) return;

    setIsSaving(true);
    setError("");
    setSuccess("");
    try {
      await updateTrainingChecklist(companyId, {
        trainings: trainings.map((training) => ({
          slug: training.slug,
          name: training.name,
          status: training.status,
          last_performed_at: training.last_performed_at,
        })),
      });
      setSuccess("Checklist atualizado com sucesso!");
      setInitialValues(trainings);
    } catch (err: any) {
      setError(err?.message || "Erro ao salvar checklist");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Box display="flex" flexDirection="column" gap={2}>
      <Typography>
        Consulte e atualize os treinamentos anuais sugeridos para manter a empresa em conformidade.
      </Typography>
      {error && <Alert severity="error">{error}</Alert>}
      {success && <Alert severity="success">{success}</Alert>}
      {isLoading ? (
        <SkeletonLoad variant="table" rows={5} columns={3} />
      ) : (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell><strong>Treinamento</strong></TableCell>
                <TableCell width={180}><strong>Status</strong></TableCell>
                <TableCell width={220}><strong>Última realização</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {trainings.map((training, index) => (
                <TableRow key={`${training.slug}-${index}`}>
                  <TableCell>{training.name}</TableCell>
                  <TableCell>
                    <TextField
                      select
                      fullWidth
                      value={training.status}
                      onChange={(e) => handleChange(index, "status", e.target.value)}
                    >
                      {statusOptions.map((option) => (
                        <MenuItem key={option} value={option}>
                          {option}
                        </MenuItem>
                      ))}
                    </TextField>
                  </TableCell>
                  <TableCell>
                    <TextField
                      type="date"
                      fullWidth
                      value={training.last_performed_at ?? ""}
                      onChange={(e) => handleChange(index, "last_performed_at", e.target.value)}
                      InputLabelProps={{ shrink: true }}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      <Box display="flex" gap={2}>
        <Button
          variant="contained"
          disabled={!hasChanges || isSaving}
          onClick={handleSave}
        >
          {isSaving ? "Salvando..." : "Salvar alterações"}
        </Button>
        <Button
          variant="outlined"
          disabled={!hasChanges || isSaving}
          onClick={() => setTrainings(initialValues)}
        >
          Desfazer alterações
        </Button>
      </Box>
    </Box>
  );
};

export default TrainingChecklistTab;
